// File: frontend/src/components/MobileBottomNav.jsx

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';

// App-style bottom tab bar for mobile, replacing the horizontally-scrolling
// tab row (which overflowed the screen with no visual hint there was more
// to scroll to). Only the most-used destinations get a permanent tab; the
// rest live behind "More" so this never needs to scroll or wrap.
export default function MobileBottomNav({ currentUser }) {
    const { t } = useLanguage();
    const [isMoreOpen, setIsMoreOpen] = useState(false);

    const primaryTabs = [
        { to: '/dashboard', icon: '🏠', label: t('nav_dashboard') },
        { to: '/calendar', icon: '📅', label: t('nav_calendar') },
        { to: '/attendance', icon: '✓', label: t('nav_attendance') },
        { to: '/leave-requests', icon: '💬', label: t('nav_leave') },
    ];

    const moreLinks = [
        { to: '/classes', icon: '📚', label: t('nav_classes') },
        { to: '/students', icon: '👥', label: t('nav_students') },
        { to: '/reports', icon: '📊', label: t('nav_reports') },
        { to: '/expenses', icon: '🧾', label: t('nav_expenses') },
        ...(['admin', 'accountant', 'teacher'].includes(currentUser.role)
            ? [{ to: '/payroll', icon: '💰', label: t('nav_payroll') }] : []),
        ...(['admin', 'accountant'].includes(currentUser.role)
            ? [{ to: '/ledger', icon: '📒', label: t('nav_ledger') }] : []),
        { to: '/profile', icon: '👤', label: 'Profile' },
    ];

    const tabClass = ({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 text-[10px] font-medium transition-colors cursor-pointer ${
            isActive ? 'text-blue-600' : 'text-gray-500 dark:text-gray-400'
        }`;

    return (
        <>
            <nav
                className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-stretch"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
            >
                {primaryTabs.map(tab => (
                    <NavLink key={tab.to} to={tab.to} className={tabClass}>
                        <span className="text-lg leading-none">{tab.icon}</span>
                        <span className="truncate max-w-[56px]">{tab.label}</span>
                    </NavLink>
                ))}
                <button
                    type="button"
                    onClick={() => setIsMoreOpen(true)}
                    className="flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 text-[10px] font-medium text-gray-500 dark:text-gray-400 cursor-pointer"
                >
                    <span className="text-lg leading-none">☰</span>
                    <span>More</span>
                </button>
            </nav>

            {isMoreOpen && (
                <div className="sm:hidden fixed inset-0 z-50">
                    <div
                        className="absolute inset-0 bg-black/40 animate-in fade-in"
                        onClick={() => setIsMoreOpen(false)}
                    />
                    <div
                        className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-2xl p-4 pb-6 animate-in slide-in-from-bottom duration-200"
                        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
                    >
                        <div className="w-10 h-1.5 bg-gray-300 rounded-full mx-auto mb-4" />
                        <div className="grid grid-cols-4 gap-3">
                            {moreLinks.map(link => (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    onClick={() => setIsMoreOpen(false)}
                                    className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/60 hover:bg-gray-100 text-gray-700 dark:text-gray-300 text-[11px] font-medium"
                                >
                                    <span className="text-xl leading-none">{link.icon}</span>
                                    <span className="truncate max-w-[64px]">{link.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
